import { useCallback } from "react"

interface UseSelectSettingsOptions<T = string> {
  /**
   * Available options for the select dropdown
   */
  options: Record<string, string>
  /**
   * Function to update the selected value
   */
  setValue: (value: T)=> void
}

/**
 * Custom hook for managing select dropdown settings
 * Provides formatted options and a unified change handler
 */
export function useSelectSettings<T = string>({
  options,
  setValue,
}: UseSelectSettingsOptions<T>) {
  const formattedOptions = Object.keys(options).map(key=> ({
    key,
    label: options[key] ?? key,
  }))

  const handleChange = useCallback(
    (value: string)=> {
      const selectedOption = Object.keys(options).find(key=> key === value)
      if (selectedOption) {
        // SAFETY: selectedOption is a key from options, which is T
        setValue(selectedOption as T)
      }
    },
    [options, setValue],
  )

  return {
    /** Change handler for select component */
    onChange: handleChange,
    /** Options formatted for select component */
    options: formattedOptions,
  }
}
